<template>
  <div class="page-outer">
    <div class="page-header page-header-fixed-form">
      <div class="header-left">
        <h1 class="page-title">{{ isEdit ? '編輯系統帳號' : '新增系統帳號' }}</h1>
      </div>
      <div class="header-actions">
        <button
          v-if="isEdit"
          class="btn btn-warning"
          @click="handleResetPassword"
          type="button"
        >
          <i class="ri-lock-password-line"></i>
          重設密碼
        </button>
        <button type="button" class="btn btn-secondary" @click="handleCancel">
          <i class="ri-close-line"></i>
          取消
        </button>
        <button type="button" class="btn btn-primary" :disabled="loading" @click="handleSubmit">
          <span v-if="loading">{{ isEdit ? '更新中...' : '建立中...' }}</span>
          <span v-else>{{ isEdit ? '更新帳號' : '建立帳號' }}</span>
        </button>
      </div>
    </div>

    <div class="page-container">
    <div class="create-account-container">

    <div class="form-container">
      <form @submit.prevent="handleSubmit">
        <div class="form-section">
          <h3 class="section-title">基本資訊</h3>
          
          <div class="form-row">
            <div class="form-group">
              <label for="username" class="required">登入帳號</label>
              <input
                id="username"
                v-model="form.username"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.username }"
                placeholder="請輸入登入帳號"
                required
                autocomplete="username"
                :disabled="isEdit"
              />
              <div v-if="errors.username" class="invalid-feedback">
                {{ errors.username }}
              </div>
              <p v-if="isEdit" class="form-hint">登入帳號建立後無法修改</p>
            </div>

            <div class="form-group">
              <label for="email" class="required">Email</label>
              <input
                id="email"
                v-model="form.email"
                type="email"
                class="form-control"
                :class="{ 'is-invalid': errors.email }"
                placeholder="請輸入 Email"
                required
                autocomplete="email"
              />
              <div v-if="errors.email" class="invalid-feedback">
                {{ errors.email }}
              </div>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="name" class="required">姓名</label>
              <input
                id="name"
                v-model="form.name"
                type="text"
                class="form-control"
                :class="{ 'is-invalid': errors.name }"
                placeholder="請輸入姓名"
                required
              />
              <div v-if="errors.name" class="invalid-feedback">
                {{ errors.name }}
              </div>
            </div>

            <div class="form-group">
              <label for="role">角色</label>
              <select
                id="role"
                v-model="form.role"
                class="form-control"
                :disabled="loadingRoles"
                :class="{ 'is-invalid': errors.role }"
              >
                <option value="">請選擇角色</option>
                <option
                  v-for="role in roles"
                  :key="role.role_code"
                  :value="role.role_code"
                >
                  {{ role.role_name }}
                </option>
              </select>
              <div v-if="errors.role" class="invalid-feedback">
                {{ errors.role }}
              </div>
              <p v-if="loadingRoles" class="form-hint">載入角色列表中...</p>
              <p v-else-if="selectedRoleDescription" class="form-hint">
                {{ selectedRoleDescription }}
              </p>
              <p v-else-if="roles.length === 0" class="form-hint text-warning">
                沒有可用的角色，請聯繫管理員
              </p>
              <p v-else class="form-hint">選擇此帳號的角色</p>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group">
              <label for="password" :class="{ required: !isEdit }">密碼</label>
              <input
                id="password"
                v-model="form.password"
                type="password"
                class="form-control"
                :class="{ 'is-invalid': errors.password }"
                :placeholder="isEdit ? '留空則不修改密碼' : '請輸入密碼（至少 6 個字元）'"
                :required="!isEdit"
                autocomplete="new-password"
                :minlength="isEdit ? 0 : 6"
              />
              <div v-if="errors.password" class="invalid-feedback">
                {{ errors.password }}
              </div>
              <p v-if="isEdit" class="form-hint">留空則不修改密碼</p>
            </div>

            <div class="form-group">
              <label for="confirmPassword" :class="{ required: !isEdit && form.password }">確認密碼</label>
              <input
                id="confirmPassword"
                v-model="form.confirmPassword"
                type="password"
                class="form-control"
                :class="{ 'is-invalid': errors.confirmPassword }"
                :placeholder="isEdit ? '留空則不修改密碼' : '請再次輸入密碼'"
                :required="!isEdit && form.password"
                autocomplete="new-password"
              />
              <div v-if="errors.confirmPassword" class="invalid-feedback">
                {{ errors.confirmPassword }}
              </div>
            </div>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">狀態設定</h3>
          
          <div class="form-group">
            <label for="status">帳號狀態</label>
            <select
              id="status"
              v-model="form.status"
              class="form-control"
            >
              <option value="active">啟用</option>
              <option value="inactive">停用</option>
            </select>
            <p class="form-hint">停用的帳號將無法登入系統</p>
          </div>
        </div>

        <div class="form-section">
          <h3 class="section-title">權限設定</h3>
          
          <div class="form-group">
            <label class="checkbox-label">
              <input
                type="checkbox"
                v-model="form.hasAllPermissions"
                class="form-checkbox"
                @change="handleHasAllPermissionsChange"
              />
              <span>擁有全部權限</span>
            </label>
            <p class="form-hint">
              勾選此選項後，帳號將擁有所有權限，無需再設定具體權限列表和程式功能
            </p>
          </div>

        </div>

        <!-- 程式功能設定 -->
        <div v-if="isEdit && !form.hasAllPermissions" class="form-section">
          <h3 class="section-title">程式功能設定</h3>
          
          <div class="programs-management">
            <div class="programs-header">
              <p class="programs-description">設定此帳號可以使用的程式功能</p>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                @click="openAddProgramsDialog"
                :disabled="loadingPrograms || !form.role"
              >
                <i class="ri-add-line"></i> 添加程式
              </button>
            </div>

            <div v-if="loadingPrograms" class="loading-state">
              <i class="ri-loader-4-line spin"></i> 載入中...
            </div>

            <div v-else-if="associatedPrograms.length === 0" class="empty-state">
              <i class="ri-folder-open-line"></i>
              <p>尚未設定任何程式功能</p>
              <button
                type="button"
                class="btn btn-sm btn-primary"
                @click="openAddProgramsDialog"
                :disabled="!form.role"
              >
                <i class="ri-add-line"></i> 添加程式
              </button>
            </div>

            <div v-else class="programs-list">
              <div
                v-for="program in associatedPrograms"
                :key="program.id"
                class="program-item"
              >
                <div class="program-info">
                  <i :class="program.icon || 'ri-file-line'" class="program-icon"></i>
                  <div class="program-details">
                    <div class="program-name">{{ program.programName }}</div>
                    <div class="program-meta">
                      <span class="program-code">{{ program.programCode }}</span>
                      <span class="program-path-small">{{ program.programPath }}</span>
                    </div>
                    <!-- 顯示此帳號允許的權限 -->
                    <div class="program-allowed-permissions">
                      <div v-if="isAllPermissionsSelected(program)" class="all-perms-allowed">
                        <i class="ri-check-line"></i>
                        <span>允許所有功能</span>
                      </div>
                      <div v-else-if="program.allowedPermissions && program.allowedPermissions.length > 0" class="allowed-perms">
                        <span class="perms-label">允許功能：</span>
                        <span v-for="perm in program.allowedPermissions" :key="perm" class="perm-badge">
                          {{ formatPermissionName(perm) }}
                        </span>
                      </div>
                      <div v-else class="all-perms-allowed">
                        <i class="ri-check-line"></i>
                        <span>允許所有功能</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="program-actions">
                  <button
                    type="button"
                    class="btn btn-sm btn-outline-primary"
                    @click="handleEditPermissions(program)"
                    title="編輯權限"
                  >
                    <i class="ri-shield-line"></i>
                    編輯權限
                  </button>
                  <button
                    type="button"
                    class="btn btn-sm btn-danger"
                    @click="handleRemoveProgram(program.id)"
                    :disabled="removingProgramId === program.id"
                  >
                    <i class="ri-delete-bin-line"></i>
                    {{ removingProgramId === program.id ? '移除中...' : '移除' }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="errorMessage" class="alert alert-danger">
          {{ errorMessage }}
        </div>
      </form>
    </div>

    <!-- 添加程式對話框 -->
    <ProgramSelector
      :show="showAddProgramsDialog"
      title="添加程式功能"
      confirm-button-text="添加"
      :available-programs="availablePrograms"
      :loading="loadingAvailablePrograms"
      :saving="addingPrograms"
      :initial-selected="{}"
      @close="showAddProgramsDialog = false"
      @confirm="handleAddPrograms"
    />

    <!-- 編輯權限對話框 -->
    <div v-if="showEditPermissionsDialog" class="modal-overlay" @click.self="showEditPermissionsDialog = false">
      <div class="modal-dialog">
        <div class="modal-header">
          <h3 class="modal-title">編輯程式權限</h3>
          <button
            type="button"
            class="btn-close"
            @click="showEditPermissionsDialog = false"
          >
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="editingProgram" class="program-info-section">
            <div class="program-info-card">
              <div class="program-header">
                <i :class="editingProgram.icon || 'ri-file-line'" class="program-icon-large"></i>
                <div class="program-details-full">
                  <div class="program-name-large">{{ editingProgram.programName }}</div>
                  <div class="program-meta-full">
                    <span class="program-code">{{ editingProgram.programCode }}</span>
                    <span class="program-path">{{ editingProgram.programPath }}</span>
                  </div>
                </div>
              </div>
            </div>

            <div class="permissions-editor">
              <div class="permissions-header">
                <span class="permissions-label">選擇允許的功能：</span>
                <button
                  type="button"
                  class="btn-link btn-sm"
                  @click="selectAllEditPermissions"
                >
                  全選
                </button>
              </div>

              <div v-if="editingProgram.permissions && editingProgram.permissions.length > 0" class="permissions-checkboxes">
                <label
                  v-for="perm in editingProgram.permissions"
                  :key="perm"
                  class="permission-checkbox-label"
                >
                  <input
                    type="checkbox"
                    :checked="editPermissionsSelected === null || (Array.isArray(editPermissionsSelected) && editPermissionsSelected.includes(perm))"
                    @change="handleEditPermissionToggle(perm)"
                    :disabled="perm.endsWith(':read') && Array.isArray(editPermissionsSelected) && editPermissionsSelected.length === 1 && editPermissionsSelected.includes(perm)"
                  />
                  <span class="permission-name">{{ formatPermissionName(perm) }}</span>
                </label>
              </div>
              <div v-else class="no-permissions">
                <i class="ri-information-line"></i>
                此程式沒有定義任何權限
              </div>

              <div class="permissions-hint">
                <i class="ri-information-line"></i>
                read 權限預設勾選且不可取消，點擊「全選」可選擇此程式的所有功能
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            @click="showEditPermissionsDialog = false"
            :disabled="updatingPermissions"
          >
            取消
          </button>
          <button
            type="button"
            class="btn btn-primary"
            @click="handleSavePermissions"
            :disabled="updatingPermissions"
          >
            <span v-if="updatingPermissions">更新中...</span>
            <span v-else>儲存</span>
          </button>
        </div>
      </div>
    </div>

    <!-- 重設密碼模態框 -->
    <div v-if="showResetPasswordModal" class="modal-overlay" @click.self="closeResetPasswordModal">
      <div class="modal-content">
        <div class="modal-header">
          <h3 class="modal-title">重設密碼</h3>
          <button class="btn-close" @click="closeResetPasswordModal">
            <i class="ri-close-line"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="form-group">
            <label for="newPassword" class="required">新密碼</label>
            <input
              id="newPassword"
              v-model="resetPasswordForm.newPassword"
              type="password"
              class="form-control"
              :class="{ 'is-invalid': resetPasswordForm.errors.newPassword }"
              placeholder="請輸入新密碼（至少 8 個字元）"
            />
            <div v-if="resetPasswordForm.errors.newPassword" class="invalid-feedback">
              {{ resetPasswordForm.errors.newPassword }}
            </div>
          </div>
          <div class="form-group">
            <label for="confirmPassword" class="required">確認密碼</label>
            <input
              id="confirmPassword"
              v-model="resetPasswordForm.confirmPassword"
              type="password"
              class="form-control"
              :class="{ 'is-invalid': resetPasswordForm.errors.confirmPassword }"
              placeholder="請再次輸入新密碼"
            />
            <div v-if="resetPasswordForm.errors.confirmPassword" class="invalid-feedback">
              {{ resetPasswordForm.errors.confirmPassword }}
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="closeResetPasswordModal">取消</button>
          <button class="btn btn-primary" @click="submitResetPassword" :disabled="resettingPassword">
            <span v-if="resettingPassword">處理中...</span>
            <span v-else>確認重設</span>
          </button>
        </div>
      </div>
    </div>
    </div>
    </div>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { systemAccountService } from '@/api/modules/systemAccounts.js';
import { rolesService } from '@/api/modules/roles.js';
import { accountProgramService } from '@/api/modules/accountPrograms.js';
import ProgramSelector from '@/components/ProgramSelector.vue';
import { showSuccess, showError, showDeleteConfirm } from '@/utils/swal.js';

export default {
  name: 'CreateSystemAccount',
  components: {
    ProgramSelector
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    const loading = ref(false);
    const errorMessage = ref('');
    const errors = reactive({});
    const roles = ref([]);
    const loadingRoles = ref(false);

    const isEdit = computed(() => {
      return !!route.params.id;
    });

    const form = reactive({
      username: '',
      email: '',
      name: '',
      password: '',
      confirmPassword: '',
      role: '',
      status: 'active',
      hasAllPermissions: true,
    });

    // 程式功能管理相關
    const loadingPrograms = ref(false);
    const associatedPrograms = ref([]);
    const showAddProgramsDialog = ref(false);
    const loadingAvailablePrograms = ref(false);
    const availablePrograms = ref([]);
    const addingPrograms = ref(false);
    const removingProgramId = ref(null);
    
    // 編輯權限相關
    const showEditPermissionsDialog = ref(false);
    const editingProgram = ref(null);
    const editPermissionsSelected = ref([]);
    const updatingPermissions = ref(false);

    const selectedRoleDescription = computed(() => {
      if (!form.role) return '';
      const role = roles.value.find(r => r.role_code === form.role);
      return role ? role.description : '';
    });

    // 載入角色列表
    const fetchRoles = async () => {
      loadingRoles.value = true;
      try {
        const response = await rolesService.getRolesByAccountType('system');
        console.log('角色列表 API 響應:', response);
        
        if (response && response.success && response.data) {
          // 處理返回的數據格式（options 端點直接返回數組）
          let rolesList = [];
          if (Array.isArray(response.data)) {
            rolesList = response.data;
          } else if (response.data.items && Array.isArray(response.data.items)) {
            rolesList = response.data.items;
          }
          
          roles.value = rolesList;
          console.log('載入的角色列表:', roles.value);
          
          // 如果沒有預設角色，設定第一個角色為預設值
          if (!isEdit.value && roles.value.length > 0 && !form.role) {
            form.role = roles.value[0].role_code;
            console.log('設定預設角色:', form.role);
          }
        } else {
          console.warn('角色列表 API 返回格式異常:', response);
        }
      } catch (err) {
        console.error('獲取角色列表失敗:', err);
        console.error('錯誤詳情:', err.response?.data || err.message);
        
        // 顯示錯誤訊息
        const errorMsg = err.response?.data?.message || err.message || '獲取角色列表失敗';
        errors.role = errorMsg;
        
        // 如果獲取失敗，使用預設值（如果沒有設定）
        if (!isEdit.value && !form.role) {
          form.role = 'system_admin';
          console.warn('使用預設角色:', form.role);
        }
      } finally {
        loadingRoles.value = false;
      }
    };

    const validateForm = () => {
      // 清除之前的錯誤
      Object.keys(errors).forEach(key => delete errors[key]);
      errorMessage.value = '';

      let isValid = true;

      // 驗證登入帳號
      if (!form.username || form.username.trim().length < 3) {
        errors.username = '登入帳號至少需要 3 個字元';
        isValid = false;
      }

      // 驗證 Email
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!form.email || !emailRegex.test(form.email)) {
        errors.email = '請輸入有效的 Email 地址';
        isValid = false;
      }

      // 驗證姓名
      if (!form.name || form.name.trim().length === 0) {
        errors.name = '請輸入姓名';
        isValid = false;
      }

      // 驗證密碼（編輯模式下，密碼是可選的）
      if (!isEdit.value) {
        // 新增模式：密碼必填
        if (!form.password || form.password.length < 6) {
          errors.password = '密碼至少需要 6 個字元';
          isValid = false;
        }

        // 驗證確認密碼
        if (form.password !== form.confirmPassword) {
          errors.confirmPassword = '兩次輸入的密碼不一致';
          isValid = false;
        }
      } else {
        // 編輯模式：如果填了密碼，則需要驗證
        if (form.password) {
          if (form.password.length < 6) {
            errors.password = '密碼至少需要 6 個字元';
            isValid = false;
          }

          if (form.password !== form.confirmPassword) {
            errors.confirmPassword = '兩次輸入的密碼不一致';
            isValid = false;
          }
        }
      }

      return isValid;
    };

    const handleSubmit = async () => {
      if (!validateForm()) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const accountData = {
          email: form.email.trim(),
          name: form.name.trim(),
          role: form.role,
          status: form.status,
          hasAllPermissions: form.hasAllPermissions,
          permissions: form.hasAllPermissions ? [] : [],
        };

        if (!isEdit.value) {
          accountData.username = form.username.trim();
        }

        // 編輯模式下，只有填了密碼才加入
        if (isEdit.value) {
          if (form.password) {
            accountData.password = form.password;
          }
        } else {
          accountData.password = form.password;
        }

        let response;
        if (isEdit.value) {
          response = await systemAccountService.updateAccount(route.params.id, accountData);
        } else {
          response = await systemAccountService.createAccount(accountData);
        }

        if (response && response.success) {
          await showSuccess(
            isEdit.value ? '更新成功' : '新增成功',
            `帳號「${form.username}」已成功${isEdit.value ? '更新' : '建立'}`
          );
          router.push({ name: 'SystemAccounts' });
        } else {
          const errorMsg = response && response.message || (isEdit.value ? '更新帳號失敗' : '新增帳號失敗');
          await showError(isEdit.value ? '更新失敗' : '新增失敗', errorMsg);
        }
      } catch (error) {
        console.error(`${isEdit.value ? '更新' : '新增'}帳號失敗:`, error);
        
        // 處理特定錯誤
        if (error.code === 'USERNAME_EXISTS') {
          errors.username = '登入帳號已存在';
        } else if (error.code === 'EMAIL_EXISTS') {
          errors.email = 'Email 已存在';
        } else {
          const errorMsg = error.message || `${isEdit.value ? '更新' : '新增'}帳號失敗，請稍後再試`;
          await showError(isEdit.value ? '更新失敗' : '新增失敗', errorMsg);
        }
      } finally {
        loading.value = false;
      }
    };

    const handleCancel = () => {
      router.push({ name: 'SystemAccounts' });
    };

    // 重設密碼相關
    const showResetPasswordModal = ref(false);
    const resettingPassword = ref(false);
    const resetPasswordForm = ref({
      newPassword: '',
      confirmPassword: '',
      errors: {}
    });

    const handleResetPassword = () => {
      resetPasswordForm.value = {
        newPassword: '',
        confirmPassword: '',
        errors: {}
      };
      showResetPasswordModal.value = true;
    };

    const closeResetPasswordModal = () => {
      showResetPasswordModal.value = false;
      resetPasswordForm.value = {
        newPassword: '',
        confirmPassword: '',
        errors: {}
      };
    };

    const validateResetPasswordForm = () => {
      const errors = {};

      if (!resetPasswordForm.value.newPassword) {
        errors.newPassword = '請輸入新密碼';
      } else if (resetPasswordForm.value.newPassword.length < 8) {
        errors.newPassword = '密碼至少需要 8 個字元';
      }

      if (!resetPasswordForm.value.confirmPassword) {
        errors.confirmPassword = '請確認密碼';
      } else if (resetPasswordForm.value.newPassword !== resetPasswordForm.value.confirmPassword) {
        errors.confirmPassword = '兩次輸入的密碼不一致';
      }

      resetPasswordForm.value.errors = errors;
      return Object.keys(errors).length === 0;
    };

    const submitResetPassword = async () => {
      if (!validateResetPasswordForm()) {
        return;
      }

      resettingPassword.value = true;

      try {
        const response = await systemAccountService.resetPassword(route.params.id, {
          newPassword: resetPasswordForm.value.newPassword
        });

        if (response && response.success) {
          await showSuccess('重設成功', '密碼已成功重設');
          closeResetPasswordModal();
        } else {
          await showError('重設失敗', response?.message || '重設密碼失敗');
        }
      } catch (err) {
        console.error('重設密碼失敗:', err);
        await showError('重設失敗', err.message || '重設密碼失敗');
      } finally {
        resettingPassword.value = false;
      }
    };

    // 處理「擁有全部權限」選項變更
    const handleHasAllPermissionsChange = async () => {
      if (form.hasAllPermissions) {
        // 如果已經設定了程式功能，提示用戶並清除
        if (isEdit.value && associatedPrograms.value.length > 0) {
          const confirmed = await showDeleteConfirm(
            '確認清除程式功能設定',
            '勾選「擁有全部權限」後，已設定的程式功能將被清除。確定要繼續嗎？'
          );
          
          if (confirmed) {
            try {
              // 清除所有已設定的程式
              for (const program of associatedPrograms.value) {
                try {
                  await accountProgramService.removeProgram(route.params.id, program.id, 'system');
                } catch (error) {
                  console.error(`移除程式 ${program.id} 失敗:`, error);
                }
              }
              
              // 立即更新帳號的 hasAllPermissions 狀態到資料庫
              await systemAccountService.updateAccount(route.params.id, {
                hasAllPermissions: true,
                permissions: []
              });
              
              await fetchAssociatedPrograms();
              await showSuccess('已清除', '程式功能設定已清除，權限狀態已更新');
            } catch (error) {
              console.error('更新權限狀態失敗:', error);
              await showError('更新失敗', '清除程式功能成功，但更新權限狀態失敗，請手動點擊「更新帳號」按鈕');
            }
          } else {
            // 用戶取消，恢復勾選狀態
            form.hasAllPermissions = false;
          }
        } else if (isEdit.value) {
          // 如果沒有程式功能需要清除，但還是要更新 hasAllPermissions 狀態
          try {
            await systemAccountService.updateAccount(route.params.id, {
              hasAllPermissions: true,
              permissions: []
            });
            await showSuccess('已更新', '權限狀態已更新為「擁有全部權限」');
          } catch (error) {
            console.error('更新權限狀態失敗:', error);
            await showError('更新失敗', '更新權限狀態失敗，請手動點擊「更新帳號」按鈕');
          }
        }
      } else {
        // 取消勾選「擁有全部權限」時，不需要立即更新資料庫
        // 等待用戶點擊「更新帳號」按鈕時再一起更新
      }
    };

    const fetchAccount = async () => {
      if (!isEdit.value) {
        return;
      }

      loading.value = true;
      errorMessage.value = '';

      try {
        const response = await systemAccountService.getAccount(route.params.id);

        if (response && response.success && response.data) {
          const account = response.data;
          form.username = account.username;
          form.email = account.email;
          form.name = account.name || '';
          form.role = account.role;
          form.status = account.status;
          if (account.hasAllPermissions === true || account.hasAllPermissions === 'true' || account.hasAllPermissions === 1) {
            form.hasAllPermissions = true;
          } else if (account.hasAllPermissions === false || account.hasAllPermissions === 'false' || account.hasAllPermissions === 0) {
            form.hasAllPermissions = false;
          }
          // 編輯模式下，密碼留空
          form.password = '';
          form.confirmPassword = '';
        } else {
          await showError('獲取失敗', response && response.message || '獲取帳號資料失敗');
          router.push({ name: 'SystemAccounts' });
        }
      } catch (error) {
        console.error('獲取帳號資料失敗:', error);
        await showError('獲取失敗', error.message || '獲取帳號資料失敗，請稍後再試');
        router.push({ name: 'SystemAccounts' });
      } finally {
        loading.value = false;
      }
    };

    // 載入帳號的程式列表
    const fetchAssociatedPrograms = async () => {
      if (!isEdit.value || !route.params.id) return;

      loadingPrograms.value = true;
      try {
        const response = await accountProgramService.getProgramsByAccountId(route.params.id, 'system');
        if (response && response.success) {
          associatedPrograms.value = response.data || [];
        }
      } catch (error) {
        console.error('載入關聯程式失敗:', error);
      } finally {
        loadingPrograms.value = false;
      }
    };

    // 載入可選程式列表
    const fetchAvailablePrograms = async () => {
      if (!isEdit.value || !route.params.id || !form.role) return;

      loadingAvailablePrograms.value = true;
      try {
        const response = await accountProgramService.getAvailablePrograms(route.params.id, form.role, 'system');
        if (response && response.success) {
          availablePrograms.value = response.data || [];
        }
      } catch (error) {
        console.error('載入可選程式失敗:', error);
        await showError('載入失敗', error.message || '載入可選程式失敗');
      } finally {
        loadingAvailablePrograms.value = false;
      }
    };

    // 打開添加程式對話框
    const openAddProgramsDialog = async () => {
      if (!form.role) {
        await showError('請先選擇角色', '需要選擇角色才能添加程式');
        return;
      }
      await fetchAvailablePrograms();
      showAddProgramsDialog.value = true;
    };

    // 添加程式到帳號
    const handleAddPrograms = async (data) => {
      if (!isEdit.value || !route.params.id) return;

      addingPrograms.value = true;
      try {
        const response = await accountProgramService.addPrograms(
          route.params.id,
          data.programIds,
          data.programPermissions,
          'system'
        );

        if (response && response.success) {
          await showSuccess('添加成功', `成功添加 ${response.data.added} 個程式功能`);
          showAddProgramsDialog.value = false;
          await fetchAssociatedPrograms();
          await fetchAvailablePrograms();
        } else {
          await showError('添加失敗', response?.message || '添加程式失敗');
        }
      } catch (error) {
        console.error('添加程式失敗:', error);
        await showError('添加失敗', error.message || '添加程式失敗');
      } finally {
        addingPrograms.value = false;
      }
    };

    // 從帳號移除程式
    const handleRemoveProgram = async (programId) => {
      if (!isEdit.value || !route.params.id) return;

      const confirmed = await showDeleteConfirm('確認移除', '確定要從此帳號移除這個程式嗎？');
      if (!confirmed) return;

      removingProgramId.value = programId;
      try {
        const response = await accountProgramService.removeProgram(route.params.id, programId, 'system');
        if (response && response.success) {
          await showSuccess('移除成功', '程式已從帳號中移除');
          await fetchAssociatedPrograms();
          await fetchAvailablePrograms();
        } else {
          await showError('移除失敗', response?.message || '移除程式失敗');
        }
      } catch (error) {
        console.error('移除程式失敗:', error);
        await showError('移除失敗', error.message || '移除程式失敗');
      } finally {
        removingProgramId.value = null;
      }
    };

    // 格式化權限名稱
    const formatPermissionName = (permission) => {
      const parts = permission.split(':');
      if (parts.length !== 2) return permission;
      
      const actionMap = {
        'read': '讀取',
        'create': '新增',
        'update': '編輯',
        'delete': '刪除',
        'export': '匯出',
        'print': '列印'
      };
      
      const resource = parts[0];
      const action = parts[1];
      
      return `${resource}:${actionMap[action] || action}`;
    };

    // 檢查是否所有權限都被勾選
    const isAllPermissionsSelected = (program) => {
      if (!program.allowedPermissions || program.allowedPermissions.length === 0) {
        return true;
      }
      
      if (!program.permissions || program.permissions.length === 0) {
        return true;
      }
      
      const allowedSet = new Set(program.allowedPermissions);
      const programSet = new Set(program.permissions);
      
      return allowedSet.size === programSet.size && 
             Array.from(programSet).every(perm => allowedSet.has(perm));
    };

    // 打開編輯權限對話框
    const handleEditPermissions = (program) => {
      editingProgram.value = program;
      if (program.allowedPermissions && program.allowedPermissions.length > 0) {
        editPermissionsSelected.value = [...program.allowedPermissions];
      } else {
        if (program.permissions && program.permissions.length > 0) {
          const readPermission = program.permissions.find(p => p.endsWith(':read'));
          editPermissionsSelected.value = readPermission ? [readPermission] : [];
        } else {
          editPermissionsSelected.value = [];
        }
      }
      showEditPermissionsDialog.value = true;
    };

    // 切換編輯權限
    const handleEditPermissionToggle = (permission) => {
      if (editPermissionsSelected.value === null) {
        if (editingProgram.value && editingProgram.value.permissions) {
          editPermissionsSelected.value = [...editingProgram.value.permissions];
        } else {
          editPermissionsSelected.value = [];
        }
      }
      
      if (!Array.isArray(editPermissionsSelected.value)) {
        editPermissionsSelected.value = [];
      }
      
      const perms = editPermissionsSelected.value;
      const index = perms.indexOf(permission);
      
      if (index > -1 && permission.endsWith(':read') && perms.length === 1) {
        return;
      }
      
      if (index > -1) {
        perms.splice(index, 1);
        if (perms.length === 0) {
          if (editingProgram.value && editingProgram.value.permissions) {
            const readPermission = editingProgram.value.permissions.find(p => p.endsWith(':read'));
            if (readPermission) {
              perms.push(readPermission);
            }
          }
        }
      } else {
        perms.push(permission);
      }
    };

    // 全選編輯權限
    const selectAllEditPermissions = () => {
      if (editingProgram.value && editingProgram.value.permissions) {
        editPermissionsSelected.value = [...editingProgram.value.permissions];
      }
    };

    // 保存權限
    const handleSavePermissions = async () => {
      if (!editingProgram.value || !isEdit.value || !route.params.id) return;

      updatingPermissions.value = true;
      try {
        let allowedPermissions;
        if (editPermissionsSelected.value === null) {
          allowedPermissions = null;
        } else if (Array.isArray(editPermissionsSelected.value) && editPermissionsSelected.value.length > 0) {
          allowedPermissions = editPermissionsSelected.value;
        } else {
          if (editingProgram.value.permissions && editingProgram.value.permissions.length > 0) {
            const readPermission = editingProgram.value.permissions.find(p => p.endsWith(':read'));
            allowedPermissions = readPermission ? [readPermission] : null;
          } else {
            allowedPermissions = null;
          }
        }

        const response = await accountProgramService.updateProgramPermissions(
          route.params.id,
          editingProgram.value.id,
          allowedPermissions,
          'system'
        );

        if (response && response.success) {
          await showSuccess('更新成功', '程式權限已更新');
          showEditPermissionsDialog.value = false;
          editingProgram.value = null;
          editPermissionsSelected.value = [];
          await fetchAssociatedPrograms();
        } else {
          await showError('更新失敗', response?.message || '更新權限失敗');
        }
      } catch (error) {
        console.error('更新權限失敗:', error);
        await showError('更新失敗', error.message || '更新權限失敗');
      } finally {
        updatingPermissions.value = false;
      }
    };

    // 監聽角色變更，重新獲取可選程式
    watch(() => form.role, async (newRole) => {
      if (newRole && isEdit.value) {
        await fetchAvailablePrograms();
      }
    });

    onMounted(async () => {
      // 先載入角色列表
      await fetchRoles();
      // 如果是編輯模式，先載入帳號資料（這樣才能獲取角色），然後再獲取程式
      if (isEdit.value) {
        await fetchAccount();
        await fetchAssociatedPrograms();
        if (form.role) {
          await fetchAvailablePrograms();
        }
      }
    });

    return {
      form,
      errors,
      errorMessage,
      loading,
      roles,
      loadingRoles,
      selectedRoleDescription,
      isEdit,
      handleHasAllPermissionsChange,
      handleSubmit,
      handleCancel,
      // 程式功能管理
      loadingPrograms,
      associatedPrograms,
      showAddProgramsDialog,
      loadingAvailablePrograms,
      availablePrograms,
      addingPrograms,
      removingProgramId,
      openAddProgramsDialog,
      handleAddPrograms,
      handleRemoveProgram,
      formatPermissionName,
      isAllPermissionsSelected,
      // 編輯權限
      showEditPermissionsDialog,
      editingProgram,
      editPermissionsSelected,
      updatingPermissions,
      handleEditPermissions,
      handleEditPermissionToggle,
      selectAllEditPermissions,
      handleSavePermissions,
      showResetPasswordModal,
      resettingPassword,
      resetPasswordForm,
      handleResetPassword,
      closeResetPasswordModal,
      submitResetPassword,
    };
  },
};
</script>

<style scoped>
.create-account-container {
  max-width: 900px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.form-container {
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.form-section {
  margin-bottom: 32px;
  padding-bottom: 32px;
  border-bottom: 1px solid #e9ecef;
}

.form-section:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-bottom: 20px;
}

.form-group {
  margin-bottom: 20px;
}

.form-group label {
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 14px;
}

.form-group label.required::after {
  content: ' *';
  color: #dc3545;
}

.form-control {
  width: 100%;
  padding: 10px 16px;
  border: 1px solid #ddd;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s;
}

.form-control:focus {
  outline: none;
  border-color: #667eea;
  box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
}

.form-control.is-invalid {
  border-color: #dc3545;
}

.invalid-feedback {
  display: block;
  color: #dc3545;
  font-size: 12px;
  margin-top: 4px;
}

.form-hint {
  font-size: 12px;
  color: #666;
  margin-top: 4px;
  margin-bottom: 0;
}

.form-group .checkbox-label {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 0;
  cursor: pointer;
  font-weight: normal;
  line-height: 1.25;
}

.form-group .form-checkbox {
  width: 16px;
  height: 16px;
  margin: 0;
  padding: 0;
  flex-shrink: 0;
  cursor: pointer;
}

.permissions-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  gap: 12px;
  margin-top: 12px;
}

.permission-item {
  padding: 8px;
  background: #f8f9fa;
  border-radius: 4px;
}

.alert {
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 20px;
  font-size: 14px;
}

.alert-danger {
  background-color: #fee;
  color: #c33;
  border: 1px solid #fcc;
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e9ecef;
}

.btn {
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.btn-primary {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.btn-primary:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

.btn-secondary {
  background: #6c757d;
  color: white;
}

.btn-secondary:hover:not(:disabled) {
  background: #5a6268;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* 程式功能管理樣式 */
.programs-management {
  margin-top: 16px;
}

.programs-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.programs-description {
  color: #6c757d;
  font-size: 14px;
  margin: 0;
}

.loading-state,
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #6c757d;
  font-size: 14px;
}

.loading-state i,
.empty-state i {
  font-size: 48px;
  margin-bottom: 12px;
  color: #adb5bd;
}

.programs-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.program-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  background: #f8f9fa;
  transition: all 0.2s;
}

.program-item:hover {
  border-color: #ced4da;
  background: #fff;
}

.program-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;
}

.btn-outline-primary {
  background: white;
  color: #007bff;
  border: 1px solid #007bff;
}

.btn-outline-primary:hover:not(:disabled) {
  background: #007bff;
  color: white;
}

.program-info {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.program-icon {
  font-size: 24px;
  color: #495057;
}

.program-details {
  flex: 1;
}

.program-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
}

.program-meta {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #6c757d;
  align-items: center;
  flex-wrap: wrap;
}

.program-code {
  padding: 2px 8px;
  background: #e9ecef;
  border-radius: 3px;
  font-family: monospace;
}

.program-path-small {
  color: #868e96;
  font-size: 11px;
}

.program-allowed-permissions {
  margin-top: 8px;
  width: 100%;
}

.allowed-perms {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
}

.perms-label {
  font-size: 12px;
  color: #495057;
  font-weight: 500;
}

.perm-badge {
  display: inline-block;
  padding: 3px 10px;
  background: #d1ecf1;
  color: #0c5460;
  border: 1px solid #bee5eb;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 500;
  white-space: nowrap;
}

.all-perms-allowed {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  background: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  width: fit-content;
}

.all-perms-allowed i {
  font-size: 14px;
}

/* 編輯權限對話框樣式 */
.program-info-section {
  margin-bottom: 20px;
}

.program-info-card {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 20px;
}

.program-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.program-icon-large {
  font-size: 32px;
  color: #495057;
}

.program-details-full {
  flex: 1;
}

.program-name-large {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin-bottom: 4px;
}

.program-meta-full {
  display: flex;
  gap: 12px;
  font-size: 12px;
  color: #6c757d;
}

.permissions-editor {
  margin-top: 20px;
}

.permissions-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.permissions-label {
  font-weight: 500;
  color: #374151;
}

.btn-link {
  background: none;
  border: none;
  color: #3b82f6;
  cursor: pointer;
  font-size: 0.875rem;
  padding: 0;
  text-decoration: underline;
}

.btn-link:hover {
  color: #2563eb;
}

.btn-link.btn-sm {
  font-size: 0.8125rem;
}

.permissions-checkboxes {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 12px;
}

.permission-checkbox-label {
  display: flex;
  align-items: center;
  gap: 6px;
  cursor: pointer;
  font-size: 0.875rem;
}

.permission-name {
  color: #374151;
}

.permissions-hint {
  font-size: 0.8125rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 6px;
}

.no-permissions {
  padding: 20px;
  text-align: center;
  color: #6b7280;
  font-size: 0.875rem;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-dialog,
.modal-content {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 80vh;
  display: flex;
  flex-direction: column;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #e9ecef;
}

.modal-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

.btn-close {
  background: none;
  border: none;
  font-size: 24px;
  color: #6c757d;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s;
}

.btn-close:hover {
  background: #f8f9fa;
  color: #333;
}

.modal-body {
  flex: 1;
  overflow-y: auto;
  padding: 20px 24px;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid #e5e7eb;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.btn-sm {
  padding: 6px 12px;
  font-size: 13px;
}

@media (max-width: 768px) {
  .form-row {
    grid-template-columns: 1fr;
  }

  .permissions-list {
    grid-template-columns: 1fr;
  }
}
</style>
